import React, { useMemo, useState } from 'react';
import { Modal, Button, Select } from 'antd';
import { TeamOutlined, UserOutlined, WarningOutlined } from '@ant-design/icons';
import './AssignNumberModal.css';
import { ISubscriptionItem } from '../../../../pages/subscriptions/controllers/useSubscriptionsPageController';
import { ContactsTransferAnswer, ContactsTransferQuestion, getContactsTransferChoices } from '../../../../hooks/subscription/useNumberAssignmentSteps';
import ContactsTransferText from '../../../@shared/number-assignment/ContactsTransferText';
import NumberAssignmentProgress from '../../../@shared/number-assignment/NumberAssignmentProgress';

interface UserOption {
  id: string;
  name: string;
}

interface AssignNumberModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  subscription: ISubscriptionItem | null;
  users: UserOption[];
  /** Asked once confirmed, when other users hold contacts on the number */
  transferQuestion?: ContactsTransferQuestion | null;
  /** The backend is moving the number and its contacts */
  isAssigning?: boolean;
  onCancel: () => void;
  onConfirm: (numberId: string, subscriptionId: string, userId: string) => void;
  onAnswerTransfer: (transfer: ContactsTransferAnswer) => void;
}

const AssignNumberModal: React.FC<AssignNumberModalProps> = ({
  isOpen,
  isLoading = false,
  subscription,
  users,
  transferQuestion = null,
  isAssigning = false,
  onCancel,
  onConfirm,
  onAnswerTransfer,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);

  // Determine if this is a reassignment (number already has a user)
  const isReassign = Boolean(subscription && subscription.usernames.length > 0);
  // Once the question is answered, the modal stays open until the number moved. Before, closing it cancels: nothing is written
  const isLocked = isAssigning;
  const transferChoices = transferQuestion ? getContactsTransferChoices(transferQuestion) : null;
  // Freeing the number asks whether its contacts stay on it
  const isFreeing = transferQuestion?.newUserName === null;

  const handleConfirm = () => {
    if (subscription && selectedUserId) {
      onConfirm(subscription.sender, subscription.id, selectedUserId);
    }
  };

  const handleCancel = () => {
    setSelectedUserId(undefined);
    onCancel();
  };

  // Filter users that can be assigned (exclude current user if reassigning)
  const userOptions = useMemo(() => [{ value: 'none', label: 'Ne pas assigner' },...users
    .filter((user: UserOption) => !subscription?.usernames.some((username: string) => username === user.name))
    .map((user: UserOption) => ({
      value: user.id,
      label: user.name,
    }))], [users, subscription?.usernames]
  );

  if (!subscription) return null;

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      centered
      width={520}
      className={`assign-number-modal ${isReassign ? 'assign-number-modal--reassign' : ''}`}
      closable={!isLocked}
      maskClosable={!isLocked}
      keyboard={!isLocked}
    >
      <div className="assign-number-modal__content">
        {/* Header with icon and title */}
        <div className="assign-number-modal__header">
          <div className="assign-number-modal__icon">
            <UserOutlined />
          </div>
          <h2 className="assign-number-modal__title">
            {isFreeing ? 'Libérer le numéro' : isReassign ? 'Réassigner le numéro' : 'Assigner le numéro'}
          </h2>
        </div>

        {/* Number info */}
        <div className="assign-number-modal__info">
          <div className="assign-number-modal__info-row">
            <span className="assign-number-modal__info-label">Numéro</span>
          </div>
          <div className="assign-number-modal__info-row">
            <span className="assign-number-modal__phone">{subscription.sender_formatted}</span>
          </div>
        </div>

        {isAssigning ? (
          <NumberAssignmentProgress />
        ) : transferQuestion && transferChoices ? (
          <>
            <div className="assign-number-modal__warning">
              <div className="assign-number-modal__warning-header">
                <TeamOutlined className="assign-number-modal__warning-icon" />
                <span className="assign-number-modal__warning-title">Contacts déjà sur ce numéro</span>
              </div>
              <p className="assign-number-modal__warning-text">
                <ContactsTransferText question={transferQuestion} />
              </p>
            </div>

            <div className="assign-number-modal__actions">
              <Button className="assign-number-modal__cancel-btn" onClick={() => onAnswerTransfer(transferChoices.secondary.answer)}>
                {transferChoices.secondary.label}
              </Button>
              <Button
                type="primary"
                className={`assign-number-modal__confirm-btn ${isReassign ? 'assign-number-modal__confirm-btn--reassign' : ''}`}
                onClick={() => onAnswerTransfer(transferChoices.primary.answer)}
              >
                {transferChoices.primary.label}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Warning section for reassignment */}
            {isReassign && (
              <div className="assign-number-modal__warning">
                <div className="assign-number-modal__warning-header">
                  <WarningOutlined className="assign-number-modal__warning-icon" />
                  <span className="assign-number-modal__warning-title">Assignement actuel</span>
                </div>
                <p className="assign-number-modal__warning-text">
                  <strong>{subscription.usernames.join(', ')}</strong> utilise actuellement le {subscription.sender_formatted}.
                  <br />
                  {/* TODO : @MrWarzo adapter selon le genre du user */}
                  Il perdra immédiatement l'accès au numéro.
                </p>
              </div>
            )}

            {/* User selection */}
            <div className="assign-number-modal__select-section">
              <label className="assign-number-modal__label">Assigner à</label>
              <Select
                placeholder="Sélectionner un utilisateur"
                value={selectedUserId}
                onChange={setSelectedUserId}
                options={userOptions}
                className="assign-number-modal__select"
                size="large"
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>

            {/* Actions */}
            <div className="assign-number-modal__actions">
              <Button className="assign-number-modal__cancel-btn" onClick={handleCancel}>
                Annuler
              </Button>
              <Button
                type="primary"
                className={`assign-number-modal__confirm-btn ${isReassign ? 'assign-number-modal__confirm-btn--reassign' : ''
                  }`}
                onClick={handleConfirm}
                loading={isLoading}
                disabled={!selectedUserId}
              >
                {isReassign ? 'Réassigner le numéro' : 'Assigner le numéro'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AssignNumberModal;
